import { useMutation, useQueryClient } from '@tanstack/react-query';

import { type CampaignBrief, suggestMediaPackage } from '~/services/MediaService';

import { mediaPackageQueryKeys } from './mediaQueryKeys';

/**
 * How long the browser will wait for a suggestion.
 *
 * svc-media allows itself four minutes for the call; this sits above that so its own error
 * reaches the page instead of the request being cut off from this side first.
 */
const SUGGESTION_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Asks svc-media to propose a base, mid and premium package for a brief.
 *
 * Each call stores all three as media packages, so the package listings are refreshed with the
 * answer.
 */
export function useSuggestMediaPackage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (brief: CampaignBrief) => {
      const { result } = await suggestMediaPackage(brief, {
        // The service spends minutes on this, so give the browser a deadline of its own —
        // slightly longer than the service's, so its error surfaces rather than being cut off.
        signal: AbortSignal.timeout(SUGGESTION_TIMEOUT_MS),
      });

      return result;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: mediaPackageQueryKeys.all }),
  });
}
